/**
 * API client for the Wholesale Workspace — Adesa Run List workflow.
 * Wraps /wholesale-auction/* endpoints with typed fetchers. Mirrors backend
 * Pydantic shapes — field names match server JSON keys with no conversion.
 *
 * Sibling to api/wholesale.ts (the Cleared List workflow); the two workflows
 * share permissions but their data flows are independent.
 */
import { API } from "../config/api"

// ---------------------------------------------------------------------------
// Primitive unions
// ---------------------------------------------------------------------------

export type AuctionVendor = "adesa_run_list" | "vauto" | "rapidrecon"

export type AuctionRunStatus = "draft" | "completed" | "failed"

// ---------------------------------------------------------------------------
// Files / session
// ---------------------------------------------------------------------------

export interface AuctionFileSummary {
  file_id: number
  vendor: AuctionVendor
  filename: string
  row_count: number
  uploaded_at: string | null
  // Rapid Recon files only: the rooftops this file's rows cover.
  rooftops_covered: string[] | null
}

export interface AuctionSession {
  adesa_run_list: AuctionFileSummary | null
  vauto: AuctionFileSummary | null
  // Rapid Recon is the multi-file slot (one per-rooftop export each). The
  // coverage fields drive the "all 6 rooftops" build gate in the UI.
  rapidrecon: AuctionFileSummary[]
  rapidrecon_rooftops_covered: string[]
  rapidrecon_rooftops_expected: string[]
  rapidrecon_rooftops_missing: string[]
  rapidrecon_ready: boolean
}

// ---------------------------------------------------------------------------
// Runs + vehicles + alerts
// ---------------------------------------------------------------------------

export interface AuctionAlert {
  code: string
  title: string
  detail: string
}

export interface AuctionUnmappedRooftop {
  display_label: string
  display_order: number
}

// How a row matched the VAUTO export. "matched" = VIN and stock agree on one
// row (green ✓); "ambiguous" = only one matched or they disagree, user picks a
// candidate (clickable red ✗); "no_match" = neither matched (plain red);
// "user_selected" = the user picked a candidate or corrected the row (blue ✓).
export type AuctionMatchState = "matched" | "ambiguous" | "no_match" | "user_selected"

// One partial-match VAUTO candidate the user can choose for an ambiguous row.
// One recon note for a car, tagged with the recon file it came from. A car can
// appear in several recon files (its per-rooftop export and the DOLANRECON
// catch-all), so the modal shows one of these per source that has a note.
export interface AuctionReconNote {
  source: string | null
  notes: string | null
}

export interface AuctionMatchCandidate {
  matched_on: "vin" | "stock"
  vauto_stock: string | null
  vauto_vin: string | null
  age: number | null
  inventory_date: string | null
  year_vauto: number | null
  make_vauto: string | null
  model_vauto: string | null
  color_vauto: string | null
  odometer_vauto: number | null
  appraiser: string | null
  vehicle_source: string | null
  vehicle_description: string | null
  kbb_trade_in: number | null
  kbb_lending: number | null
  unit_cost: number | null
  reconditioning_cost: number | null
  pack_cost: number | null
  other_cost: number | null
  transportation_cost: number | null
  wholesale_status: string | null
  vauto_store: string | null
  // Rapid Recon notes pre-tagged for this candidate's stock — recon follows the
  // VAUTO pick, so choosing this candidate also attaches these notes.
  rr_notes: string | null
  rr_stock: string | null
  rr_rooftop: string | null
  rr_matches: AuctionReconNote[] | null
}

export interface AuctionVehicle {
  id: number
  run_id: number
  // Adesa side
  lane: string | null
  run_number: number | null
  client_id: string | null
  adesa_stock: string | null
  vin: string | null
  year_adesa: number | null
  make_adesa: string | null
  model_adesa: string | null
  color_adesa: string | null
  odo_adesa: number | null
  store: string | null
  // VAUTO side (null when no match)
  vauto_stock: string | null
  age: number | null
  inventory_date: string | null
  year_vauto: number | null
  make_vauto: string | null
  model_vauto: string | null
  color_vauto: string | null
  odometer_vauto: number | null
  appraiser: string | null
  vehicle_source: string | null
  vehicle_description: string | null
  kbb_trade_in: number | null
  kbb_lending: number | null
  unit_cost: number | null
  reconditioning_cost: number | null
  pack_cost: number | null
  other_cost: number | null
  transportation_cost: number | null
  wholesale_status: string | null
  vauto_store: string | null
  // Match state (VAUTO)
  match_state: AuctionMatchState | null
  match_candidates: AuctionMatchCandidate[] | null
  // Rapid Recon notes (follow the VAUTO-resolved identity)
  rr_match_state: AuctionMatchState | null
  rr_notes: string | null
  rr_stock: string | null
  rr_rooftop: string | null
  rr_matches: AuctionReconNote[] | null
  // Cost-inclusion flags (drive total_cost; default unit-only)
  include_unit_cost: boolean
  include_reconditioning_cost: boolean
  include_pack_cost: boolean
  include_other_cost: boolean
  include_transportation_cost: boolean
  include_extra_cost: boolean
  // Editable
  extra_cost: number | null
  sold: number | null
  high_bid: number | null
  best_when: string | null
  notes: string | null
  store_override: string | null
  // Computed
  rooftop_label: string | null // the rooftop the disposition routes to
  total_cost: number | null
  disposition: number | null
  disposition_per_rooftop: Record<string, number | null>
  alerts: AuctionAlert[]
}

export interface AuctionRunSummary {
  id: number
  auction_date: string | null
  status: AuctionRunStatus
  started_at: string | null
  completed_at: string | null
  initiated_by_user_id: number
  initiated_by_name: string | null
  adesa_filename: string | null
  adesa_row_count: number | null
  vauto_filename: string | null
  vauto_row_count: number | null
  vehicle_count: number
}

export interface AuctionRunDetail {
  run: AuctionRunSummary
  rooftops: string[]
  unmapped_rooftops: AuctionUnmappedRooftop[]
  vehicles: AuctionVehicle[]
}

export interface AuctionRunListResponse {
  runs: AuctionRunSummary[]
}

export interface AuctionVehicleEditRequest {
  // Costs — all editable in V2 (extra_cost is shown as "Adjustments (±)")
  unit_cost?: number | null
  reconditioning_cost?: number | null
  pack_cost?: number | null
  other_cost?: number | null
  transportation_cost?: number | null
  extra_cost?: number | null
  // Cost-inclusion flags
  include_unit_cost?: boolean
  include_reconditioning_cost?: boolean
  include_pack_cost?: boolean
  include_other_cost?: boolean
  include_transportation_cost?: boolean
  include_extra_cost?: boolean
  // Rooftop override (a rooftop label) + auction results
  store_override?: string | null
  sold?: number | null
  high_bid?: number | null
  best_when?: string | null
  notes?: string | null
}

// ---------------------------------------------------------------------------
// Auth helper (copied-in-shape from wholesale.ts; consolidation is a
// project-wide cleanup item flagged in frontend-standards §7).
// ---------------------------------------------------------------------------

const TOKEN_KEY = "invi_token"

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ""
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    let detail = ""
    try {
      const body = await res.json()
      if (body && typeof body.detail === "string") {
        detail = body.detail
      }
    } catch {
      // fall through
    }
    if (!detail) {
      try {
        detail = await res.text()
      } catch {
        // fall through to status
      }
    }
    throw new Error(detail || `HTTP ${res.status}`)
  }
  return res
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const E = API.endpoints.wholesaleAuction

export async function getAuctionSession(): Promise<AuctionSession> {
  const res = await authFetch(`${API.baseUrl}${E.session}`)
  return res.json()
}

export async function uploadAuctionFile(
  file: File,
  overrideVendor?: AuctionVendor,
): Promise<AuctionFileSummary> {
  const formData = new FormData()
  formData.append("file", file)
  if (overrideVendor) formData.append("override_vendor", overrideVendor)

  // FormData uploads must NOT set Content-Type: application/json — let
  // the browser pick the multipart boundary. So we don't go through
  // authFetch (which forces JSON content-type) for this one call.
  const res = await fetch(`${API.baseUrl}${E.upload}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData,
  })
  if (!res.ok) {
    let detail = ""
    try {
      const body = await res.json()
      if (body && typeof body.detail === "string") detail = body.detail
    } catch {
      // fall through
    }
    if (!detail) detail = (await res.text()) || `HTTP ${res.status}`
    throw new Error(detail)
  }
  return res.json()
}

export async function deleteAuctionFile(fileId: number): Promise<void> {
  await authFetch(`${API.baseUrl}${E.deleteFile(fileId)}`, {
    method: "DELETE",
  })
}

// Remove every uploaded file for the caller, resetting the upload workspace.
// Used by "Start New Run" so the next auction starts from a clean slate.
export async function clearAuctionSession(): Promise<void> {
  await authFetch(`${API.baseUrl}${E.clearFiles}`, {
    method: "DELETE",
  })
}

export async function buildAuctionRun(
  dealerGroupId?: number | null,
): Promise<AuctionRunSummary> {
  const qs = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await authFetch(`${API.baseUrl}${E.runs}${qs}`, {
    method: "POST",
  })
  return res.json()
}

export async function listAuctionRuns(
  limit = 50,
  dealerGroupId?: number | null,
): Promise<AuctionRunListResponse> {
  const qs = dealerGroupId != null ? `&dealer_group_id=${dealerGroupId}` : ""
  const res = await authFetch(`${API.baseUrl}${E.runs}?limit=${limit}${qs}`)
  return res.json()
}

export async function getAuctionRun(runId: number): Promise<AuctionRunDetail> {
  const res = await authFetch(`${API.baseUrl}${E.runById(runId)}`)
  return res.json()
}

export async function completeAuctionRun(runId: number): Promise<AuctionRunSummary> {
  const res = await authFetch(`${API.baseUrl}${E.completeRun(runId)}`, {
    method: "POST",
  })
  return res.json()
}

export async function reopenAuctionRun(runId: number): Promise<AuctionRunSummary> {
  const res = await authFetch(`${API.baseUrl}${E.reopenRun(runId)}`, {
    method: "POST",
  })
  return res.json()
}

export async function editAuctionVehicle(
  vehicleId: number,
  updates: AuctionVehicleEditRequest,
): Promise<AuctionVehicle> {
  const res = await authFetch(`${API.baseUrl}${E.editVehicle(vehicleId)}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  })
  return res.json()
}

export async function selectAuctionCandidate(
  vehicleId: number,
  candidateStock: string,
): Promise<AuctionVehicle> {
  const res = await authFetch(`${API.baseUrl}${E.selectCandidate(vehicleId)}`, {
    method: "POST",
    body: JSON.stringify({ candidate_stock: candidateStock }),
  })
  return res.json()
}

export async function exportAuctionRunXlsx(runId: number): Promise<Blob> {
  const url = `${API.baseUrl}${E.exportXlsx(runId)}`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `Export failed: ${res.status}`)
  }
  return res.blob()
}
